import React, { useCallback, useEffect, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';

const OVERALL = {
  COMPLETED_SUPPORTED_PROJECTION: ['Completed (supported sales projection)', 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'],
  REBUILD_RUNNING: ['Rebuild running', 'bg-sky-500/15 text-sky-300 border-sky-500/30'],
  REBUILD_WAITING: ['Rebuild waiting for another worker', 'bg-sky-500/15 text-sky-300 border-sky-500/30'],
  OWNERSHIP_LOST: ['Rebuild lost ownership — previous projection kept', 'bg-amber-500/15 text-amber-300 border-amber-500/30'],
  ABANDONED: ['Last rebuild was abandoned by a stopped process — previous projection kept', 'bg-amber-500/15 text-amber-300 border-amber-500/30'],
  BUILD_FAILED: ['Build failed — previous projection kept', 'bg-rose-500/15 text-rose-300 border-rose-500/30'],
  DATABASE_UNAVAILABLE: ['Database unavailable during rebuild', 'bg-rose-500/15 text-rose-300 border-rose-500/30'],
  SETTLEMENT_RECONSTRUCTION_PENDING: ['Settlement reconstruction pending', 'bg-amber-500/15 text-amber-300 border-amber-500/30'],
  SETTLEMENT_EVIDENCE_CONFLICT: ['Settlement evidence conflict', 'bg-rose-500/15 text-rose-300 border-rose-500/30'],
  PARTIALLY_BUILT: ['Some days never built', 'bg-amber-500/15 text-amber-300 border-amber-500/30'],
  NOT_BUILT: ['Not built for this window', 'bg-zinc-800 text-zinc-300 border-zinc-600'],
};

const inr = (v) => (v === null || v === undefined ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

function ConflictRow({ c }) {
  return (
    <div className="grid grid-cols-[90px_1fr] gap-2 text-[11px] py-1.5 border-t border-zinc-800/60" data-testid={`cash-conflict-${c.match_id}`}>
      <span className="font-mono text-zinc-400">{c.business_date}</span>
      <div className="text-zinc-300">
        <span className="font-semibold text-rose-300" data-testid="cash-conflict-code">{c.code}</span>
        {' · '}txn <span className="font-mono">{c.sc_transaction_id || '—'}</span>
        {' · '}settlement row <span className="font-mono">{c.settlement_row_id || '—'}</span>
        {' · '}settled {inr(c.actual_settlement_amount)}
        {c.source_paid_amount !== undefined && <> · source now {inr(c.source_paid_amount)} vs matched {inr(c.matched_collectible_amount)}</>}
        {c.rails && <> · rails {c.rails.join(', ')}</>}
        {c.manual_override && <Badge className="ml-1 text-[9px] bg-zinc-800 border-zinc-700">manual</Badge>}
      </div>
    </div>
  );
}

export default function CashProjectionStatusPanel({ dateFrom, dateTo, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const r = await accountsAPI.getCashProjectionStatus({ date_from: dateFrom, date_to: dateTo });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail?.message || 'Projection status unavailable');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (error) return <div className="text-xs text-rose-300" data-testid="cash-projection-status-error">{error}</div>;
  if (!data) return null;
  const [label, cls] = OVERALL[data.overall] || [data.overall, 'bg-zinc-800 text-zinc-300 border-zinc-600'];
  const ok = data.overall === 'COMPLETED_SUPPORTED_PROJECTION';
  const Icon = ok ? CheckCircle2 : data.overall === 'SETTLEMENT_EVIDENCE_CONFLICT' ? ShieldAlert : AlertTriangle;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-2" data-testid="cash-projection-status">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className={`w-4 h-4 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} />
        <span className="text-xs text-zinc-400">Cash projection</span>
        <Badge className={`text-[10px] border ${cls}`} data-testid="cash-projection-overall">{label}</Badge>
        {Object.entries(data.day_counts || {}).map(([k, v]) => (
          <Badge key={k} className="text-[10px] border bg-zinc-900 border-zinc-700 text-zinc-300" data-testid={`cash-projection-days-${k.toLowerCase()}`}>
            {k.replace(/_/g, ' ').toLowerCase()}: {v}
          </Badge>
        ))}
        <Button variant="ghost" size="sm" className="h-6 px-2 ml-auto" onClick={load} data-testid="cash-projection-refresh">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>
      <p className="text-[10px] text-zinc-500" data-testid="cash-projection-coverage-note">{data.coverage_note}</p>
      {data.conflicts?.length > 0 && (
        <div data-testid="cash-conflict-list">
          <div className="text-[11px] text-zinc-400">
            {data.conflicts.length} settlement evidence conflict(s) — affected days are held as INCOMPLETE; nothing was auto-resolved.
          </div>
          {data.conflicts.map((c) => <ConflictRow key={`${c.match_id}-${c.business_date}`} c={c} />)}
          <div className="text-[10px] text-zinc-500 pt-1" data-testid="cash-conflict-resolution">
            {data.can_resolve_conflicts
              ? `Resolve via ${data.resolution_path}; the next rebuild re-derives realisation.`
              : 'Your role can view these conflicts; an ORG_ADMIN or OWNER resolves them in Settlement Matching.'}
          </div>
        </div>
      )}
    </div>
  );
}
